import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useAtomValue, useSetAtom } from "jotai";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import {
  recorderErrorAtom,
  recorderGeneratedMarkdownAtom,
  recorderStateAtom,
  recorderUploadProgressAtom,
  type RecorderState,
} from "@/atoms/test-recorder-atoms";
import { mainTipTapEditorAtom } from "../tip-tap-editor/tipTapEditor";
import { markdownToHtml } from "@/components/markdown-test-editor/markdown-conversion";
import {
  convertVideoToMarkdown,
  createRecordingUploadTarget,
  putRecordingToStorage,
} from "@/utils/test-recorder-upload";
import { trackSentryException } from "@/utils/helpers";

export interface RecordingPayload {
  blob: Blob;
  durationMs: number;
  fps: number;
  hasAudio: boolean;
}

interface RecordingPreviewDialogProps {
  open: boolean;
  organisationId: string;
  recording: RecordingPayload | null;
  onClose: () => void;
  onRecordAgain: () => void;
}

function statusLabel(state: RecorderState, progress: number): string {
  switch (state) {
    case "uploading":
      return `Uploading recording… ${Math.round(progress * 100)}%`;
    case "analysing":
      return "Analysing video and generating test definition...";
    case "ready":
      return "Generated test definition";
    case "error":
      return "Something went wrong";
    default:
      return "Ready to generate";
  }
}

const RecordingPreviewDialog: React.FC<RecordingPreviewDialogProps> = ({
  open,
  organisationId,
  recording,
  onClose,
  onRecordAgain,
}) => {
  const setRecorderState = useSetAtom(recorderStateAtom);
  const setRecorderError = useSetAtom(recorderErrorAtom);
  const setUploadProgress = useSetAtom(recorderUploadProgressAtom);
  const setGeneratedMarkdown = useSetAtom(recorderGeneratedMarkdownAtom);

  const recorderState = useAtomValue(recorderStateAtom);
  const recorderError = useAtomValue(recorderErrorAtom);
  const uploadProgress = useAtomValue(recorderUploadProgressAtom);
  const markdown = useAtomValue(recorderGeneratedMarkdownAtom);

  const mainEditor = useAtomValue(mainTipTapEditorAtom);

  const videoUrl = useMemo(
    () => (recording ? URL.createObjectURL(recording.blob) : null),
    [recording]
  );
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const previewEditor = useEditor(
    {
      extensions: [StarterKit.configure({ trailingNode: false })],
      content: "",
      editable: false,
      editorProps: {
        attributes: {
          class:
            "max-w-none w-full prose prose-invert focus:outline-none prose-p:m-0 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h1:mt-8 prose-h2:mt-7 prose-h3:mt-6 prose-h1:mb-2 prose-h2:mb-2 prose-h3:mb-1",
        },
      },
    },
    []
  );

  // Sync the generated markdown into the read-only preview editor whenever
  // it changes. We deliberately recreate the document on every change so
  // selection state resets cleanly between generations.
  useEffect(() => {
    if (!previewEditor) return;
    if (markdown) {
      previewEditor.commands.setContent(markdownToHtml(markdown));
    } else {
      previewEditor.commands.setContent("");
    }
  }, [markdown, previewEditor]);

  const previewSelectionEmpty = useEditorState({
    editor: previewEditor,
    selector: (ctx) => ctx.editor?.state.selection.empty ?? true,
  });

  const abortRef = useRef<AbortController | null>(null);
  const objectPathRef = useRef<string | null>(null);

  const cleanupUploadedObject = useCallback(async () => {
    // The recording lives at test-recordings/<org>/<id>.webm; if the user
    // discards before generation succeeds we leave the GCS object for the
    // bucket lifecycle rule to sweep — there's no client-side delete API
    // in this flow and the lifecycle rule (30 days) keeps the blast
    // radius bounded.
    objectPathRef.current = null;
  }, []);

  const generate = useCallback(async () => {
    if (!recording) return;
    setRecorderError(null);
    setUploadProgress(0);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let phase: "upload" | "analyse" = "upload";
    try {
      setRecorderState("uploading");
      const upload = createRecordingUploadTarget(organisationId);
      objectPathRef.current = upload.object_path;

      await putRecordingToStorage({
        objectPath: upload.object_path,
        blob: recording.blob,
        contentType: recording.blob.type || "video/webm",
        signal: controller.signal,
        onProgress: (fraction) => setUploadProgress(fraction),
      });

      phase = "analyse";
      setRecorderState("analysing");
      const result = await convertVideoToMarkdown({
        orgId: organisationId,
        objectPath: upload.object_path,
        durationMs: recording.durationMs,
        fps: recording.fps,
        hasAudio: recording.hasAudio,
        mimeType: recording.blob.type || "video/webm",
      });

      if (!result) {
        throw new Error("The conversion returned an empty result.");
      }
      setGeneratedMarkdown(result);
      setRecorderState("ready");
    } catch (e: any) {
      if (e?.name === "AbortError") {
        return;
      }
      trackSentryException(e);
      const message =
        e?.response?.data?.detail ??
        e?.message ??
        "Conversion failed. Please try again.";
      setRecorderError({ stage: phase, message });
      setRecorderState("error");
    }
  }, [
    organisationId,
    recording,
    setGeneratedMarkdown,
    setRecorderError,
    setRecorderState,
    setUploadProgress,
  ]);

  // Kick off generation as soon as the dialog opens with a fresh recording.
  // The user can still re-record / discard from the controls, but the
  // dominant action — getting the markdown back — happens automatically.
  const startedForBlobRef = useRef<Blob | null>(null);
  useEffect(() => {
    if (!open || !recording) return;
    if (startedForBlobRef.current === recording.blob) return;
    startedForBlobRef.current = recording.blob;
    setGeneratedMarkdown(null);
    setRecorderError(null);
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recording]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    void cleanupUploadedObject();
    setRecorderState("idle");
    setGeneratedMarkdown(null);
    setRecorderError(null);
    setUploadProgress(0);
    startedForBlobRef.current = null;
    onClose();
  }, [
    cleanupUploadedObject,
    onClose,
    setGeneratedMarkdown,
    setRecorderError,
    setRecorderState,
    setUploadProgress,
  ]);

  const handleRecordAgain = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRecorderState("idle");
    setGeneratedMarkdown(null);
    setRecorderError(null);
    setUploadProgress(0);
    startedForBlobRef.current = null;
    onRecordAgain();
  }, [
    onRecordAgain,
    setGeneratedMarkdown,
    setRecorderError,
    setRecorderState,
    setUploadProgress,
  ]);

  const handleReplace = useCallback(() => {
    if (!mainEditor || !markdown) return;
    const docIsEmpty = mainEditor.state.doc.textContent.trim().length === 0;
    if (
      !docIsEmpty &&
      !window.confirm(
        "Replace the existing test definition with the generated one? You can undo this with Cmd/Ctrl-Z."
      )
    ) {
      return;
    }
    mainEditor.commands.setContent(markdownToHtml(markdown));
    handleClose();
  }, [mainEditor, markdown, handleClose]);

  const handleInsertSelection = useCallback(() => {
    if (!previewEditor || !mainEditor) return;
    const { from, to } = previewEditor.state.selection;
    if (from === to) return;
    const slice = previewEditor.state.doc.slice(from, to);
    mainEditor
      .chain()
      .focus()
      .insertContentAt(
        mainEditor.state.selection.from,
        slice.content.toJSON()
      )
      .run();
    handleClose();
  }, [previewEditor, mainEditor, handleClose]);

  const isBusy =
    recorderState === "uploading" || recorderState === "analysing";

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isBusy && reason === "backdropClick") return;
        handleClose();
      }}
      fullWidth
      maxWidth="lg"
      aria-labelledby="test-recorder-preview-dialog"
    >
      <DialogTitle id="test-recorder-preview-dialog" sx={{ pr: 6 }}>
        Generated test from recording
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isBusy}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 360px) 1fr" },
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recording
              {recording?.hasAudio === false && " (no audio)"}
            </Typography>
            {videoUrl ? (
              <video
                controls
                src={videoUrl}
                style={{ width: "100%", borderRadius: 8, background: "#000" }}
              />
            ) : (
              <Box sx={{ height: 200, bgcolor: "grey.100", borderRadius: 1 }} />
            )}
            <Box sx={{ mt: 2 }}>
              <Tooltip title="Discard this recording and start a new one">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={handleRecordAgain}
                    disabled={isBusy}
                  >
                    Re-record
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 360 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {statusLabel(recorderState, uploadProgress)}
            </Typography>
            {recorderState === "uploading" && (
              <LinearProgress
                variant="determinate"
                value={Math.round(uploadProgress * 100)}
                sx={{ mb: 2 }}
              />
            )}
            {recorderState === "analysing" && (
              <LinearProgress sx={{ mb: 2 }} />
            )}
            {recorderState === "error" && recorderError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={generate}>
                    Retry
                  </Button>
                }
              >
                {recorderError.message}
              </Alert>
            )}
            <Box
              sx={{
                flex: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                overflow: "auto",
                bgcolor: "#313131",
                minHeight: 280,
                position: "relative",
              }}
            >
              {recorderState === "ready" && previewEditor ? (
                <EditorContent editor={previewEditor} />
              ) : isBusy ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "rgba(255, 255, 255, 0.92)",
                  }}
                >
                  <CircularProgress
                    size={24}
                    sx={{ mr: 1, color: "rgba(255, 255, 255, 0.92)" }}
                  />
                  Generating test definition…
                </Box>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  The generated markdown will appear here.
                </Typography>
              )}
            </Box>
            {recorderState === "ready" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Select any portion of the generated markdown above and click
                <strong> Insert selection at cursor</strong> to splice just
                that into your editor.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: "flex-end" }}>
        <Tooltip title="Discard this recording without changing the editor">
          <span>
            <Button onClick={handleClose} disabled={isBusy}>
              Discard
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Insert only the highlighted markdown at the cursor in your editor">
          <span>
            <Button
              variant="outlined"
              onClick={handleInsertSelection}
              disabled={
                recorderState !== "ready" ||
                previewSelectionEmpty ||
                !mainEditor
              }
            >
              Insert selection at cursor
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Replace the test editor content with the generated markdown">
          <span>
            <Button
              variant="contained"
              onClick={handleReplace}
              disabled={recorderState !== "ready" || !mainEditor}
            >
              Replace editor content
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default RecordingPreviewDialog;
